import { faStar, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FunctionComponent } from 'react'
import styled from 'styled-components'
import { TextWithIcon } from '../pages/VoterDashboard/components/VoterDashboardStyled'
import { colors, spacing } from '../styles/_var'
import { ContestantWithBio, ICategory, IVotingRound } from '../utils/interfaces'
import Button from './Button'
import Link from './Link'
import { Avatar } from './StyledComponents'
import Text, { BoldText } from './Text'
import { beautifyDate, beautifyTime, plurify } from '../utils/helpers'
import votingRoundStore from '../stores/votingRoundStore'


interface IVotingRoundBoard extends IVotingRound {
  getContestant: (username: string) => ContestantWithBio | undefined,
  actionButtonLabel: string,
  actionButtonHandler: () => void,
  selectedCategory: ICategory,
  onSetWinner?: (voting_round_id: number, contestant_id: number, category_id: number) => void,
}

const VotingRoundBoard: FunctionComponent<IVotingRoundBoard> = ({
  id,
  label,
  active_contestants=[],
  restartable,
  has_ended,
  getContestant,
  start_date,
  end_date,
  actionButtonLabel,
  has_started,
  actionButtonHandler,
  onSetWinner,
  selectedCategory
}) => {
  const isExpired = has_ended && !restartable
  const hasNotStarted = !has_started && !isExpired
  const hasStarted = has_started && !isExpired

  return (
    <BoardWrapper role="widget" aria-label={`voting round ${label}`}>
      <TitleSection>
        <BoldText variant="medium">Voting Round - {label}</BoldText>
      </TitleSection>
      {active_contestants.map((activeContestant) => {
        const contestant = getContestant(activeContestant.user)
        const isWinner = votingRoundStore.isWinner(contestant?.id || 0, selectedCategory, id)
        if (!contestant) return null;

        return (
          <React.Fragment key={activeContestant.user}>
            <ContestantRow 
              evicted={activeContestant.evicted}
              winner={isWinner}
            >
              <ContestantRowContent>
                <AvatarAndName>
                  <Avatar
                    src={contestant.avatar}
                    width={35}
                    height={35}
                    borderColor={colors.lightgrey} />
                  <Text variant="regular">
                    {contestant.first_name} {contestant.last_name}
                  </Text>
                  {isWinner && (
                    <FontAwesomeIcon icon={faStar} color="gold" size="2x" style={{ marginLeft: '8px' }} />
                  )}
                </AvatarAndName>
                <BoldText variant="regular" color={colors.purple}>
                  {plurify('Vote', activeContestant.votes)}
                </BoldText>
              </ContestantRowContent>
              {has_ended && onSetWinner && contestant.id && activeContestant.category_id && (
                <SetWinnerLink 
                  to="#" 
                  onClick={() => onSetWinner(id, contestant.id!, activeContestant.category_id!)}
                >
                  <Text variant="subtext" center>
                    {!isWinner ? 'Set as Winner' : 'Unset Winner'}
                  </Text>
                </SetWinnerLink>
              )}
            </ContestantRow>
          </React.Fragment>
        )
      })}
      <FooterSection>
        {!isExpired ? (
          <Button onClick={actionButtonHandler}>
            <BoldText variant="regular">{actionButtonLabel}</BoldText>
          </Button>
        ) : (
          <StyledTextWithIcon>
            <FontAwesomeIcon icon={faTimesCircle} size="3x" />
            <Text ml={2} variant="medium">Voting Round Closed</Text>
          </StyledTextWithIcon>
        )}
        {hasNotStarted && (
          <BoldText variant="regular">Starts {beautifyDate(start_date)} - {beautifyTime(start_date)}</BoldText>
        )}
        {hasStarted && !!end_date && (
          <BoldText variant="regular">Ends {beautifyDate(end_date)} - {beautifyTime(end_date)}</BoldText>
        )}
      </FooterSection>
    </BoardWrapper>
  )
}

const BoardWrapper = styled.div`
  width: 30%;
  max-width: 50rem;
  min-width: 35rem;
  background-color: ${colors.verylightpurple};

  border: solid 1px ${colors.lightgrey};
  border-radius: 5px;
`

const TitleSection = styled.div`
  padding: ${spacing.xs};
  border-bottom: solid 1px ${colors.lightgrey};
`

const ContestantRow = styled(({ evicted, winner, ...rest}) => <div {...rest} />)`
  border-bottom: solid 1px ${colors.lightgrey};
  position: relative;
  ${({ evicted }) => evicted && `
    filter: grayscale(100%);
  `}
  ${({ winner }) => winner && `
    background-color: ${colors.subtlylightpurple};
  `}
`

const ContestantRowContent = styled.div`
  padding: ${spacing.xs};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StyledTextWithIcon = styled(TextWithIcon)`
  color: ${colors.red};
  opacity: .9;
`

const AvatarAndName = styled.div`
  display: flex;
  align-items: center;

  img {
    margin-right: ${spacing.xs};
  }
`

const FooterSection = styled.div`
  padding: ${spacing.xs};
  text-align: center;

  button {
    min-width: 20rem;
    padding-top: 3px;
    padding-bottom: 3px;
  }
`

const SetWinnerLink = styled(Link)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.1s linear;
  
  ${ContestantRow}:hover & {
    opacity: 1;
  }
`

export default VotingRoundBoard